import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-4" data-testid="reset-no-token">
        <div className="bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-[#D05A4A] mx-auto mb-3" />
          <h2 className="text-xl font-bold text-[#1A2620] mb-2">Invalid reset link</h2>
          <p className="text-sm text-[#5C6B63] mb-5">This reset link is missing its token. Please request a new one.</p>
          <Link to="/forgot-password" className="inline-block bg-[#2C4A3B] hover:bg-[#3E6652] text-white rounded-md px-5 py-2 text-sm font-medium">Request new link</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (pw !== pw2) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pw });
      toast.success(data.message || 'Password updated.');
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-8 max-w-md w-full">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#2C4A3B] rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">🍄</span>
          </div>
          <h1 className="text-2xl font-extrabold text-[#1A2620] font-['Manrope']">Mushroom Farm</h1>
        </div>

        {done ? (
          <div className="text-center space-y-3" data-testid="reset-done">
            <div className="w-14 h-14 bg-[#EDF5EE] rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-[#3A7D44]" />
            </div>
            <h2 className="text-lg font-bold text-[#1A2620]">Password updated</h2>
            <p className="text-sm text-[#5C6B63]">You can now log in with your new password. Redirecting…</p>
            <Link to="/login" className="inline-block bg-[#2C4A3B] hover:bg-[#3E6652] text-white rounded-md px-5 py-2 text-sm font-medium mt-2">Go to login</Link>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold text-[#1A2620] mb-1">Set a new password</h2>
            <p className="text-sm text-[#5C6B63] mb-5">Choose a strong password (at least 6 characters).</p>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <label className="block text-sm font-semibold text-[#1A2620] mb-2">New password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B63]" />
                  <input
                    type="password"
                    value={pw}
                    onChange={e => setPw(e.target.value)}
                    className="w-full rounded-md border border-[#E4DFD3] bg-white pl-9 pr-3 py-2.5 text-sm text-[#1A2620] focus:border-[#2C4A3B] focus:outline-none focus:ring-1 focus:ring-[#2C4A3B]"
                    placeholder="Enter new password"
                    minLength={6}
                    required
                    data-testid="reset-pw-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A2620] mb-2">Confirm new password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B63]" />
                  <input
                    type="password"
                    value={pw2}
                    onChange={e => setPw2(e.target.value)}
                    className="w-full rounded-md border border-[#E4DFD3] bg-white pl-9 pr-3 py-2.5 text-sm text-[#1A2620] focus:border-[#2C4A3B] focus:outline-none focus:ring-1 focus:ring-[#2C4A3B]"
                    placeholder="Re-enter new password"
                    minLength={6}
                    required
                    data-testid="reset-pw-confirm"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#2C4A3B] hover:bg-[#3E6652] text-white rounded-md px-4 py-2.5 font-medium text-sm disabled:opacity-50"
                data-testid="reset-submit"
              >
                {submitting ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
